import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", tries: 0 });
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const { currency } = useCurrency();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async (tries = 0) => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid" });
          setOrder(data.order);
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ status: "failed" });
          return;
        }
        if (tries >= 25) { setState({ status: "timeout" }); return; }
        setTimeout(() => poll(tries + 1), 2000);
      } catch {
        if (tries >= 3) setState({ status: "failed" });
        else setTimeout(() => poll(tries + 1), 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main className="pt-40 pb-32 px-6 md:px-12 lg:px-16 max-w-3xl mx-auto text-center" data-testid="payment-success">
      {state.status === "polling" && (
        <>
          <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">Just a moment</p>
          <h1 className="font-serif text-5xl mt-4">Confirming your payment…</h1>
          <p className="mt-4 text-[#4A4A4A]">Please do not close this window.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">Thank you</p>
          <h1 className="font-serif text-5xl mt-4">Your order is placed.</h1>
          <p className="mt-4 text-[#4A4A4A]">
            {order && <>Order <span className="font-mono-alt">{order.order_id}</span> — {formatPrice(order.total_inr, currency)}</>}
          </p>
          <p className="mt-2 text-[#4A4A4A]">We’ll email you when it’s dispatched. Every piece is inspected by hand before it ships.</p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/" className="btn-editorial-ghost">Continue shopping</Link>
            <Link to="/account" className="btn-editorial">View orders</Link>
          </div>
        </>
      )}
      {(state.status === "failed" || state.status === "timeout") && (
        <>
          <h1 className="font-serif text-5xl mt-4">Payment couldn’t be confirmed.</h1>
          <p className="mt-4 text-[#4A4A4A]">If you were charged, we’ll email you within a few minutes. You can also retry checkout.</p>
          <Link to="/checkout" className="mt-8 btn-editorial inline-block">Try again</Link>
        </>
      )}
    </main>
  );
}
